import Awesomplete from "awesomplete";
import delegate from "es6-delegate";
import { toast } from "@clarkinc/zest-design-system/ui";
import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import { addBreadcrumb } from "@wss/error-tracking";
import deburr from "lodash/deburr";
import { z } from "zod";

type Label = string | { name: string; description: string; link: string };

interface SectionResult {
  label: Label;
  value: {
    href?: string;
    first?: boolean;
    resultType?: string;
    userProvided?: boolean;
    action?: string;
  };
}

interface CustomAwesomplete extends Awesomplete {
  sort?: boolean;
  filter?: () => boolean;
  item?: (suggestion: SectionResult, searchTerm: string) => HTMLLIElement;
  maxItems?: number;
  replace?: (suggestion: SectionResult) => void;
  autoFirst?: boolean;
}

type Results = {
  keywords?: Label[];
  prods?: Label[];
  products?: Label[];
  history?: Label[];
  categories?: Label[];
  vendors?: Label[];
};

interface SearchHistory {
  name: string;
  link?: string;
}

let canInputTooLongErrorShow = true;

const showInputTooLongError = () => {
  if (canInputTooLongErrorShow) {
    canInputTooLongErrorShow = false;

    toast.error("Please enter 100 characters or less, and try again.", {
      title: "Search is too long.",
      autoDismiss: false,
      onClose: () => {
        setTimeout(() => {
          canInputTooLongErrorShow = true;
        }, 4000);
      },
    });
  }
};

const escapeRegExCharacters = (string: string) => {
  // sourced from https://stackoverflow.com/questions/3115150/how-to-escape-regular-expression-special-characters-using-javascript
  return string.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
};

enum ValidationErrorTypes {
  InvalidCharacters = "InvalidCharacters",
  TooLong = "TooLong",
  TooShort = "TooShort",
}

const inputSchema = z.preprocess(
  (val) => {
    if (typeof val === "string") {
      // Normalize away any accented characters/umlauts
      return deburr(val);
    }
    return val;
  },
  z.coerce
    .string()
    .max(100, ValidationErrorTypes.TooLong)
    .min(2, ValidationErrorTypes.TooShort)
    .regex(/^[a-zA-Z0-9 '"&/._-]+$/, ValidationErrorTypes.InvalidCharacters),
);

export function markSearchTerm(query: string, resultText: string): string {
  return resultText.replace(
    RegExp(escapeRegExCharacters(query.trim()), "gi"),
    "<mark class='bg-transparent font-light text-inherit'>$&</mark>",
  );
}

export default class SearchAutocomplete {
  results: Results | undefined;

  resultTypes = ["keywords", "categories", "products", "vendors"] as const;

  count = 0;

  input: HTMLInputElement;

  submitButton: HTMLButtonElement;

  awesomplete: CustomAwesomplete;

  combinedResults: SectionResult[] = [];

  enableStorefrontAutosuggest: boolean;

  get form() {
    return this.input.closest("form");
  }

  get flatResults() {
    this.combinedResults = [
      {
        label: this.input.value,
        value: { userProvided: true },
      },
    ];

    this.addResults();
    return this.combinedResults;
  }

  constructor(
    input: HTMLInputElement,
    submitButton: HTMLButtonElement,
    enableStorefrontAutosuggest: boolean,
  ) {
    // Awesomplete steals focus for some reason, this allows us to reset focus back to the input after it is initialized.
    const hasFocus = document.activeElement === input;

    this.input = input;
    this.submitButton = submitButton;
    this.enableStorefrontAutosuggest = enableStorefrontAutosuggest;

    input.addEventListener("input", this.search);
    input.addEventListener("focus", this.showSearchHistory);
    this.awesomplete = new Awesomplete(this.input, {
      list: [],
      autoFirst: true,
      minChars: 0,
    });

    this.awesomplete.sort = false;
    this.awesomplete.filter = () => true;
    this.awesomplete.item = SearchAutocomplete.createSearchResult;
    this.awesomplete.maxItems = 50;
    this.awesomplete.replace = this.action;

    if (hasFocus) this.input.focus();

    delegate(this.form, "mouseover", "[data-id='result']", this.goto);
  }

  /**
   * Set the current autocomplete (search results) list in awesomplete.  Resets selection state to avoid consistency issues within awesomplete.goto
   *
   * @param newList The new list of elements to forward to awesomplete
   */
  setAwesompleteList = (newList: Awesomplete.Suggestion[]) => {
    // reset internal awesomplete variables not automatically reset when the list updates
    // this should avoid a race condition in mouseover events caused by getting a fetch response and a mouseover while the UI is hung on a slow process.
    // See RCA in ADO for details: https://tfs.clarkinc.biz/DefaultCollection/Webstaurant.StoreFront/_workitems/edit/1234728
    this.awesomplete.index = -1;

    this.awesomplete.list = newList;
  };

  goto = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    let result = event.target;

    if (
      (result.tagName === "B" || result.tagName === "MARK") &&
      result.parentElement
    )
      result = result.parentElement;
    const listItem = result.parentElement;
    if (!listItem || !listItem.parentElement) return;
    const index = [...listItem.parentElement.children].indexOf(listItem);
    try {
      this.awesomplete.goto(index);
    } catch (e) {
      addBreadcrumb(
        {
          message:
            "Encountered error in SearchAutocomplete mouseover. This will recover on its own and can happen while the list is refreshing on slow devices, but may indicate other issues with Awesomplete state management.",
          data: {
            error: toError(e),
          },
        },
        "error",
      );
    }
  };

  search = async (event: Event): Promise<Throwable<boolean>> => {
    const validatedInput = inputSchema.safeParse(this.input.value);

    if (!validatedInput.success) {
      const shouldShowSearchHistory =
        validatedInput?.error?.issues?.[0]?.message ==
          ValidationErrorTypes.TooShort && !this.input.value.length;

      if (shouldShowSearchHistory) {
        this.showSearchHistory(event);
      } else {
        this.combinedResults = [];
        this.setAwesompleteList([]);
      }

      if (
        validatedInput?.error?.issues?.[0]?.message ==
        ValidationErrorTypes.TooLong
      ) {
        showInputTooLongError();
      }

      return {
        isError: false,
        value: false,
      };
    }

    this.awesomplete.autoFirst = true;

    if (!event.target) {
      return {
        isError: false,
        value: false,
      };
    }

    this.count += 1;
    const countAtTheTimeOfSearch = this.count;

    const query = encodeURIComponent(validatedInput.data);
    let searchUrl;

    if (this.enableStorefrontAutosuggest) {
      searchUrl = `/autosuggest/${query.toLowerCase()}`;
    } else {
      searchUrl = `/wss/autosuggest/?queryText=${query}`;
    }

    try {
      const results = await fetch(searchUrl).then((response) =>
        response.json<Results>(),
      );

      if (countAtTheTimeOfSearch < this.count) {
        return {
          isError: false,
          value: false,
        };
      }

      this.results = results;

      // Normalize products property name.
      this.results.products = results.prods;
      this.results.keywords = results.keywords?.filter(
        (keyword) =>
          typeof keyword === "string" &&
          keyword.toLowerCase() !==
            `<b>${validatedInput.data}</b>`.toLowerCase(),
      );

      if (this.flatResults[0]?.label === "") {
        this.showSearchHistory(event);

        return {
          isError: false,
          value: false,
        };
      }
      this.setAwesompleteList(this.flatResults);

      return {
        isError: false,
        value: true,
      };
    } catch (e) {
      const error = new Error(
        "Error fetching and formatting global searchbar autocomplete.",
        {
          cause: toError(e),
        },
      );

      return {
        error,
        isError: true,
      };
    }
  };

  showSearchHistory = (event: Event) => {
    // if user enters input with existing text, call search method
    if (this.input.value.length > 1) {
      this.search(event);
      return;
    }
    this.awesomplete.autoFirst = false;

    this.combinedResults = [];
    let localStorageHistory;
    try {
      localStorageHistory = localStorage.getItem("searchHistory");
    } catch (e) {
      console.error(e);
    }
    if (!localStorageHistory) return;

    const searchHistory = JSON.parse(localStorageHistory);

    if (!this.results) this.results = {};

    this.results.history = searchHistory;
    this.addResultsFor("history");
    this.combinedResults.push({
      label: "Clear Search History",
      value: {
        action: "clearSearchHistory",
      },
    });
    this.setAwesompleteList(this.combinedResults);
  };

  updateSearchHistory = (
    newSearch: SearchHistory,
    searchHistory: SearchHistory[],
  ) => {
    const previousSearchIndex = searchHistory.findIndex((search) => {
      return search.name === newSearch.name;
    });

    if (previousSearchIndex > -1) {
      searchHistory.splice(previousSearchIndex, 1);
    }

    searchHistory.unshift(newSearch);

    if (searchHistory.length > 5) {
      searchHistory.splice(-1, 1);
    }
    import("storage-factory").then(({ storageFactory }) => {
      const storage = storageFactory(() => window.localStorage);

      storage.setItem("searchHistory", JSON.stringify(searchHistory));
    });
  };

  action = (suggestion: SectionResult) => {
    let searchHistoryLocalStorage;
    try {
      searchHistoryLocalStorage = localStorage.getItem("searchHistory");
    } catch (e) {
      console.error(e);
    }
    if (!searchHistoryLocalStorage) searchHistoryLocalStorage = "[]";
    const searchHistory: SearchHistory[] = JSON.parse(
      searchHistoryLocalStorage,
    );

    if (
      suggestion.value.action &&
      suggestion.value.action === "clearSearchHistory"
    ) {
      import("storage-factory").then(({ storageFactory }) => {
        const storage = storageFactory(() => window.localStorage);

        storage.setItem("searchHistory", "");
      });
      return;
    }

    if (suggestion.value.href) {
      this.updateSearchHistory(
        {
          name:
            typeof suggestion.label === "string"
              ? suggestion.label
              : suggestion.label?.name,
          link: suggestion.value.href,
        },
        searchHistory,
      );

      window.location.href = suggestion.value.href;

      return;
    }
    if (
      !suggestion.value.userProvided &&
      typeof suggestion.label === "string"
    ) {
      const linkName = suggestion.label.replace("<b>", "").replace("</b>", "");
      this.input.value = linkName;
    }

    this.updateSearchHistory(
      {
        name:
          typeof suggestion.label === "string"
            ? suggestion.label
            : suggestion.label?.name,
      },
      searchHistory,
    );

    this.submitButton.click();
  };

  static createSearchResult(suggestion: SectionResult, searchTerm: string) {
    const li = document.createElement("li");
    li.setAttribute(
      "data-recommendation-type",
      suggestion.value.resultType ? suggestion.value.resultType : "",
    );
    li.className =
      "flex relative first:border-t-0 [&:nth-child(2)]:border-t-0 group";

    if (!suggestion.value.userProvided) {
      const result = markSearchTerm(searchTerm, suggestion.toString());
      li.innerHTML = `<span class="basis-28 pt-2 pr-1 pl-3 capitalize [text-shadow:0_1px_#ffffff] text-base leading-4 font-semibold bg-gray-50 text-gray-800">${
        suggestion.value.first ? suggestion.value.resultType : ""
      }</span><span class="group text-grey-800 py-2 px-2.5 group-[&[aria-selected='true']]:text-white focus:text-white text-base leading-4 font-bold border-0 border-l border-solid border-gray-200 cursor-pointer whitespace-normal flex-1 group-[&[aria-selected='true']]:bg-green-500" data-id='result'>${result}</span>`;
    }

    if (suggestion.value.first)
      li.classList.add(
        "border-0",
        "border-t",
        "border-solid",
        "border-gray-200",
      );

    if (suggestion.value.action === "clearSearchHistory") {
      li.classList.add("clear", "text-green-500", "relative", "flex");
    }

    return li;
  }

  addResults() {
    this.resultTypes.forEach(this.addResultsFor);
  }

  addResultsFor = (resultType: keyof Results) => {
    if (!this.results) return;

    const sectionResults: SectionResult[] = [];

    this.results[resultType]?.forEach((result) => {
      let href;

      if (result.hasOwnProperty("link")) href = result.link as string; // eslint-disable-line no-prototype-builtins
      sectionResults.push({
        label: SearchAutocomplete.extractResultName(result),
        value: { href, resultType },
      });
    });

    if (sectionResults[0]) {
      sectionResults[0].value.first = true;
    }
    this.combinedResults = this.combinedResults.concat(sectionResults);
  };

  static extractResultName(label: Label) {
    if (typeof label === "string") return label;
    return label.name || label.description;
  }
}
